import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { SymbolGroup } from 'types/canvas';
import CanvasSymSelector from 'components/canvas/components/CanvasSymSelector';
import { GroupColorPicker } from './GroupColorPicker';
import { CanvasGroupWatchlistButton } from './CanvasGroupWatchlistButton';
import type { GroupSymOption } from './useGroupSymbolOptions';

const NAME_MAX = 40;

interface GroupEditSheetProps {
  open: boolean;
  group: SymbolGroup | null;
  currentSym: string;
  symOptions: GroupSymOption[];
  validSymbols: Set<string>;
  onClose: () => void;
  onRename: (name: string) => void;
  onRecolor: (color: string) => void;
  onPickSym: (sym: string) => void;
  onDelete: () => void;
}

const SheetLabel = ({ children }: { children: string }) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
      }}
    >
      {children}
    </Typography>
  );
};

/**
 * Mobile editor for the active group: name, symbol, watchlist, color, delete —
 * the same actions as the desktop inline editor, in a full-screen sheet.
 */
export const GroupEditSheet = ({
  open,
  group,
  currentSym,
  symOptions,
  validSymbols,
  onClose,
  onRename,
  onRecolor,
  onPickSym,
  onDelete,
}: GroupEditSheetProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [name, setName] = useState(group?.name ?? '');

  useEffect(() => {
    if (open) {
      setName(group?.name ?? '');
    }
  }, [open, group?.name]);

  if (group == null) {
    return null;
  }

  const commitName = () => {
    const trimmed = name.trim().slice(0, NAME_MAX);
    if (trimmed.length > 0 && trimmed !== group.name) {
      onRename(trimmed);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2.5 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 1.5 }}
      >
        <WorkspacesIcon sx={{ color: group.color, fontSize: 22 }} />
        <Box sx={{ flex: 1, fontSize: 17, fontWeight: 700 }}>Edit group</Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack sx={{ gap: 3, pt: 1 }}>
          <Stack sx={{ gap: 1 }}>
            <SheetLabel>Name</SheetLabel>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              inputProps={{ maxLength: NAME_MAX }}
              size="small"
              fullWidth
            />
          </Stack>

          <Stack sx={{ gap: 1 }}>
            <SheetLabel>Symbol</SheetLabel>
            <Stack direction="row" alignItems="center" gap={1}>
              <CanvasSymSelector
                sym={currentSym}
                options={symOptions}
                onSymChange={onPickSym}
              />
              <CanvasGroupWatchlistButton
                validSymbols={validSymbols}
                onPick={onPickSym}
              />
            </Stack>
          </Stack>

          <Stack sx={{ gap: 1.5 }}>
            <SheetLabel>Color</SheetLabel>
            <GroupColorPicker value={group.color} onChange={onRecolor} />
          </Stack>

          <Button
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={onDelete}
            sx={{ alignSelf: 'flex-start', textTransform: 'none', mt: 1 }}
          >
            Delete group
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default GroupEditSheet;
